import { useRef, useState } from "react";
import { AlertTriangle, RotateCcw, Maximize2 } from "lucide-react";
import MediaFrame from "@/components/MediaFrame";
import { useLang } from "@/lib/i18n";
import { reportMediaError, classifyMediaError } from "@/lib/readerEvents";
import { sendQualifiedView, videoViewQualifies } from "@/lib/views";

/**
 * Shared article video player: space reserved (16/9) before metadata, then the
 * real aspect (portrait videos are height-capped and centered), native controls
 * (play/seek/volume/fullscreen), no autoplay, contain (no crop), Arabic error
 * state with retry. Used for admin-uploaded and Telegram videos alike.
 */
export default function VideoPlayer({ src, poster, className = "", slug, articleId, defaultAspect = "16 / 9" }) {
    const { t } = useLang();
    const videoRef = useRef(null);
    const watch = useRef({ sec: 0, last: null, counted: false });
    const onTime = (e) => {   // real playback only: pauses, buffering and seeks add nothing; hidden tab adds nothing
        const v = e.target, w = watch.current, now = v.currentTime;
        if (w.last != null && !v.paused && !v.seeking && document.visibilityState === "visible") { const d = now - w.last; if (d > 0 && d < 1.5) w.sec += d; }
        w.last = now;
        if (!w.counted && articleId && videoViewQualifies(w.sec, Number.isFinite(v.duration) ? v.duration : 0)) { w.counted = true; sendQualifiedView(articleId, "video", w.sec); }
    };
    const [failed, setFailed] = useState(false);
    const [nonce, setNonce] = useState(0);
    const [dims, setDims] = useState(null);

    const retry = () => {
        setFailed(false); setDims(null); setNonce((n) => n + 1);
        setTimeout(() => { try { videoRef.current?.load(); } catch (_) { /* noop */ } }, 60);
    };
    const fullscreen = () => {
        const v = videoRef.current; if (!v) return;
        if (v.requestFullscreen) v.requestFullscreen().catch(() => {});
        else if (v.webkitEnterFullscreen) v.webkitEnterFullscreen();   // iOS Safari
    };

    const loaded = !!dims;
    const portrait = loaded && dims.w < dims.h;
    return (
        <MediaFrame aspect={loaded ? `${dims.w} / ${dims.h}` : defaultAspect} portrait={portrait || (!loaded && defaultAspect === "9 / 16")} loaded={loaded} maxVh={72}
            className={`relative rounded-lg overflow-hidden bg-black ${className}`} data-testid="article-video-frame" data-portrait={portrait ? "1" : "0"}>
            <video
                key={nonce} ref={videoRef} src={src} controls playsInline preload="metadata" autoPlay={false} loop={false} poster={poster}
                className="absolute inset-0 w-full h-full" style={{ objectFit: "contain", backgroundColor: "#000" }}
                onLoadedMetadata={(e) => setDims({ w: e.target.videoWidth, h: e.target.videoHeight })}
                onTimeUpdate={onTime} onSeeking={() => { watch.current.last = null; }} onPause={() => { watch.current.last = null; }} onWaiting={() => { watch.current.last = null; }}
                onError={(e) => { setFailed(true); reportMediaError({ slug, url: src, kind: "video", errorKind: classifyMediaError(e.currentTarget, "video"), detail: `code=${e.currentTarget?.error?.code ?? "?"}` }); }} data-testid="article-video">
                <source src={src} type="video/mp4" />
            </video>
            {failed && (   // temporary error: the player, poster and native controls stay visible at full size; this is just a notice
                <div className="absolute inset-x-2 bottom-12 z-[2] flex items-center justify-between gap-3 rounded-md bg-black/75 text-white px-3 py-2 text-xs" data-testid="article-video-error" role="status">
                    <span className="inline-flex items-center gap-2 min-w-0"><AlertTriangle className="w-4 h-4 text-brand-red shrink-0" /><span className="truncate">{t("videoFail")} — {t("videoFailHint")}</span></span>
                    <button type="button" onClick={retry} className="inline-flex items-center gap-1 bg-brand-red px-2.5 py-1 rounded-sm font-bold shrink-0" data-testid="article-video-retry"><RotateCcw className="w-3.5 h-3.5" /> {t("retry")}</button>
                </div>
            )}
            <button type="button" onClick={fullscreen} aria-label={t("fullscreen")} className="glow-target absolute top-2 end-2 h-9 w-9 rounded-md bg-black/55 text-white inline-flex items-center justify-center md:hidden" data-testid="video-fullscreen"><Maximize2 className="w-4 h-4" /></button>
        </MediaFrame>
    );
}
